/**
 * <s-tabs> — accessible tabs that switch between collection panels.
 *
 * Required markup:
 *   [role="tablist"]  containing [role="tab"] buttons with aria-controls.
 *   [role="tabpanel"] elements whose ids match the tabs' aria-controls.
 *
 * Roving tabindex: Left/Right (RTL-aware), Home and End move focus and select.
 */
class STabs extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (this.tabs.length === 0) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index), opts);
      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index), opts);
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onKeydown(event, index) {
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const back = isRtl ? 'ArrowRight' : 'ArrowLeft';
    const last = this.tabs.length - 1;
    let next = null;
    if (event.key === forward) next = index === last ? 0 : index + 1;
    else if (event.key === back) next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.#select(next);
    this.tabs[next].focus();
  }

  #select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = !selected;
    });
  }
}

if (!customElements.get('s-tabs')) {
  customElements.define('s-tabs', STabs);
}
